/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */

import { Component, DestroyRef, OnInit, effect, inject, input, output } from '@angular/core';
import { Subject } from 'rxjs';
import { AIAgentService } from '../../services/ai-agent.service';

/**
 * Chat message interface
 */
interface ChatMessage {
    id: string;
    type: 'bot' | 'user';
    timestamp: Date;
    content: any;
}

/**
 * Chat History List Component
 * 
 * Displays a scrollable list of previous chat conversations.
 * Features:
 * - Lazy loading with infinite scroll
 * - Date-based grouping and formatting
 * - Message preview with truncation
 * - Click to load previous conversation
 * 
 * @example
 * <app-chat-history-list 
 *   [showAIAgent]="isOpen"
 *   (chatHistorySelected)="onHistorySelect($event)">
 * </app-chat-history-list>
 */
@Component({
    selector: 'app-chat-history-list',
    templateUrl: './chat-history-list.component.html',
    styleUrl: './chat-history-list.component.scss',
    standalone: false
})
export class ChatHistoryListComponent implements OnInit {
    readonly aiAgentService = inject(AIAgentService);
    private readonly destroyRef = inject(DestroyRef);

    /** Controls when to load chat history (waits for true value) */
    readonly showAIAgent = input(false);

    /** Emits when a chat history item is selected */
    readonly chatHistorySelected = output<any>();

    /** Subject for managing subscription cleanup */
    private destroy$ = new Subject<void>();

    /** Array of chat messages */
    messages: any[];

    /** Tracks if data has been loaded to prevent duplicate API calls */
    private hasLoadedData = false;

    /** Full list of chat history items */
    chatHistoryList: any[];

    /** Initial number of history items to display */
    readonly initialHistoryCount: number = 10;

    /** Number of items to load per scroll increment */
    private readonly itemsPerPage: number = 10;

    /** Current number of displayed items */
    displayedCount: number = this.initialHistoryCount;

    /** Indicates if more items are being loaded */
    isLoading: boolean = false;

    /** Indicates if initial history fetch is in progress */
    isLoadingHistory: boolean;

    constructor() {
        // Register cleanup on destroy
        this.destroyRef.onDestroy(() => {
            this.destroy$.next();
            this.destroy$.complete();
        });

        // React to showAIAgent changes
        effect(() => {
            const shouldShow = this.showAIAgent();
            if (shouldShow && !this.hasLoadedData) {
                this.isLoadingHistory = true;
                this.getChatHistoryList();
            }
        });
    }

    /**
     * Initialize component
     */
    ngOnInit(): void {
        // Initialization is now handled by the effect in constructor
    }

    /**
     * Get history items currently displayed (paginated view)
     * @returns Sliced array of history items
     */
    get displayedHistoryList(): any[] {
        if (!this.chatHistoryList || this.chatHistoryList.length === 0) {
            return [];
        }

        return this.chatHistoryList.slice(0, this.displayedCount);
    }

    /**
     * Check if there are more items available to load
     * @returns True if more items exist beyond current display
     */
    get hasMoreItems(): boolean {
        return this.chatHistoryList && this.displayedCount < this.chatHistoryList.length;
    }

    /**
     * Handle infinite scroll - load more items when scrolled to bottom
     */
    onScroll(event: Event): void {
        const element = event.target as HTMLElement;
        const threshold = 50; // Load more when 50px from bottom

        if (element.scrollTop + element.scrollHeight >= element.scrollHeight - threshold &&
            this.hasMoreItems &&
            !this.isLoading) {
            this.loadMoreItems();
        }
    }

    /**
     * Load more chat history items
     */
    private loadMoreItems(): void {
        if (this.isLoading || !this.hasMoreItems) {
            return;
        }

        this.isLoading = true;

        // Simulate loading delay
        setTimeout(() => {
            this.displayedCount = Math.min(
                this.displayedCount + this.itemsPerPage,
                this.chatHistoryList.length
            );
            this.isLoading = false;
        }, 300);
    }

    /**
     * Fetch chat history from service
     * Note: Currently a no-op as the service method is unavailable
     */
    getChatHistoryList(): void {
        // this.aiAgentService.getConversationHistoryList()
        //     .pipe(takeUntil(this.destroy$))
        //     .subscribe({
        //         next: (historyList) => {
        //             // Process the history list with display properties and filter out items with null titles
        //             this.chatHistoryList = (historyList || [])
        //                 .filter(item => item.Title != null && item.Title !== '') // Remove items with null, undefined, or empty titles
        //                 .map(item => ({
        //                     ...item,
        //                     displayDate: this.formatDate(item.lastMessageOn)
        //                 }));

        //             this.displayedCount = Math.min(this.initialHistoryCount, this.chatHistoryList.length);

        //             this.hasLoadedData = true;
        //             this.isLoadingHistory = false;
        //         },
        //         error: (error) => {
        //             console.error('Error fetching chat history in chat-history-list:', error);
        //             this.isLoadingHistory = false;
        //             this.chatHistoryList = []; // Reset on error
        //             this.hasLoadedData = true; // Mark as loaded even on error to prevent retry
        //         }
        //     });
    }

    /**
     * Generate a unique ID for chat messages
     */
    private generateId(): string {
        return Math.random().toString(36).substr(2, 9) + Date.now();
    }

    /**
     * Parse the AI assistant's response content
     */
    private parseResponse(content: any): any {
        // If the content is already structured, return as is
        if (typeof content === 'object' && content !== null) {
            return content;
        }
        // Otherwise, wrap as plain text
        return {
            type: 'text',
            data: content || ''
        };
    }

    /**
     * Scroll the chat view to the bottom
     */
    private scrollToBottom(): void {
        // Implementation depends on the template.
        // This is a placeholder for actual scrolling logic.
        // For example, you might use ViewChild to get the chat container and scroll it.
    }



    /**
     * Load a specific chat conversation
     * Emits the selected history item to parent component
     * 
     * @param historyItem - The chat history item to load
     */
    loadChatHistory(historyItem: any): void {
        this.chatHistorySelected.emit(historyItem);
    }

    /**
     * Get display content from the last message in a conversation
     * Truncates long messages and removes system text like "siteId selected"
     * 
     * @param historyItem - History item containing messages
     * @returns Truncated message content for display
     */
    getLastMessageContent(historyItem: any): string {
        if (!historyItem?.messages || historyItem.messages.length === 0) {
            return 'Empty conversation';
        }

        const lastMessage = historyItem.messages[historyItem.messages.length - 1];
        let content = lastMessage?.content || 'No content';

        // Remove the siteId string pattern using regex
        // Pattern matches: "siteId selected by user is [any number]"
        content = content.replace(/siteId selected by user is \d+/gi, '').trim();

        // If content is empty after removing siteId string, show fallback
        if (!content) {
            content = 'Previous conversation';
        }

        // Truncate long messages for display
        return content.length > 60 ? content.substring(0, 60) + '...' : content;
    }

    /**
     * Track function for ngFor performance optimization
     * 
     * @param index - Item index
     * @param item - Chat history item
     * @returns Unique identifier for the item
     */
    trackByChatId(index: number, item: any): string {
        return item.id || item.chatId || index.toString();
    }

    /**
     * Format date into human-readable relative text
     * Examples: "Today", "Yesterday", "Previous 5 Days", "January 2024"
     * 
     * @param date - Date to format
     * @returns Formatted date string
     */
    formatDate(date: any): string {
        if (!date) return '';

        const chatDate = new Date(date);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - chatDate.getTime());
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return 'Today';
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays >= 2 && diffDays <= 30) {
            return `Previous ${diffDays} Days`;
        } else {
            // For dates older than 30 days, show month name and year
            return chatDate.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
            });
        }
    }

    /**
     * Handle chat history item selection
     * Extracts ID and emits to parent component
     * 
     * @param historyItem - Selected history item
     */
    onChatHistoryItemSelected(historyItem: any): void {
        // Extract the ID and emit it to the parent (ai-agent-bot)
        const chatId = historyItem.id || historyItem.chatId;
        if (chatId) {
            this.chatHistorySelected.emit(historyItem);
        }
    }

}